import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Ticket, Timer, ChevronRight, Camera } from "lucide-react";
import { fmt, fmtRate, rateOf } from "../lib/format";
import { useMT } from "../lib/miniI18n";
import { renderTicketCard, sharePng } from "../lib/billCard";
import { useAuth } from "../context/AuthContext";

const hm = (s) => String(s || "00:00").split(":").map(Number);
const pad = (n) => String(n).padStart(2, "0");
const to12 = (t) => { const [h, m] = hm(t); return `${((h + 11) % 12) + 1}:${pad(m)} ${h < 12 ? "AM" : "PM"}`; };
const left = (s) => { const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), x = s % 60; return h ? `${h}h ${pad(m)}m` : `${pad(m)}:${pad(x)}`; };

/* Customer's pending tickets for the given shows, pinned at the top with a live countdown to each draw. */
export function BookedStrip({ games, bets, testId = "booked-strip" }) {
  const navigate = useNavigate();
  const mt = useMT();
  const { user } = useAuth();
  const shareTicket = async (g) => {
    try {
      const url = renderTicketCard({ game: g.game.name, draw_time: g.draw_time, draw_date: g.bets[0]?.draw_date, bets: g.bets, total: g.total, name: user?.name, mobile: user?.mobile });
      await sharePng(url, `ticket_${g.game.name.replace(/\s+/g, "_")}_${g.draw_time.replace(":", "")}.png`, `🎟️ ROYAL LAKSHMI — my ticket · ${g.game.name} · draw ${g.draw_time}\n${[...new Set(g.bets.map((b) => `${b.box_of ? `BOX ${b.box_of}` : b.number} @${fmtRate(rateOf(b))}×${b.quantity || 1}`))].join(", ")} · ${fmt(g.total)}\nPlay: ${window.location.origin}`);
    } catch {}
  };
  const [now, setNow] = useState(Date.now());
  const offset = useRef(0);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  useEffect(() => {
    const st = games.find((g) => g.runtime?.server_time)?.runtime.server_time;
    if (st) offset.current = new Date(String(st).slice(0, 23)).getTime() - Date.now();
  }, [games]);

  const byId = Object.fromEntries(games.map((g) => [g.id, g]));
  const pending = (bets || []).filter((b) => b.status === "pending" && byId[b.game_id]);
  if (!pending.length) return null;
  const groups = Object.values(pending.reduce((acc, b) => {
    const k = `${b.game_id}|${b.draw_time || ""}`;
    (acc[k] = acc[k] || { game: byId[b.game_id], draw_time: b.draw_time || byId[b.game_id].draw_time, bets: [], total: 0 }).bets.push(b);
    acc[k].total += b.amount || 0;
    return acc;
  }, {})).sort((a, b) => a.draw_time.localeCompare(b.draw_time));
  const ist = new Date(now + offset.current);
  const dayStart = new Date(ist.getFullYear(), ist.getMonth(), ist.getDate()).getTime();
  const tickets = pending.reduce((a, b) => a + (b.quantity || 1), 0);

  return (
    <div className="rl-card p-3 border-emerald-200 bg-emerald-50/60" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-black tracking-wider text-emerald-900 flex items-center gap-1.5"><Ticket className="w-4 h-4 text-emerald-600" />{mt("YOUR BOOKINGS")} <span className="text-emerald-700/70 font-semibold">· {tickets} {mt(tickets === 1 ? "ticket" : "tickets")}</span></div>
        <button onClick={() => navigate("/my-bets")} className="text-[10px] font-bold text-emerald-700 flex items-center" data-testid="booked-strip-all">{mt("All bets")}<ChevronRight className="w-3 h-3" /></button>
      </div>
      <div className="space-y-2">
        {groups.map((g) => {
          const [h, m] = hm(g.draw_time);
          const secs = Math.max(0, Math.round((dayStart + (h * 60 + m) * 60000 - ist.getTime()) / 1000));
          const nums = [...new Map(g.bets.map((b) => { const n = b.box_of ? `BOX ${b.box_of}` : b.number; return [`${n}@${rateOf(b)}`, { n, r: rateOf(b) }]; })).values()];
          return (
            <button key={`${g.game.id}-${g.draw_time}`} onClick={() => navigate(`/play/${g.game.id}`)} className="w-full text-left rounded-xl bg-white border border-emerald-100 px-3 py-2 active:scale-[0.99] transition-transform" data-testid={`booked-show-${g.game.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[12px] font-bold text-slate-900 truncate">{g.game.name}</div>
                  <div className="text-[10px] text-slate-500">{mt("Draw")} {to12(g.draw_time)} · {g.bets.length} {mt(g.bets.length === 1 ? "bet" : "bets")} · {fmt(g.total)}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[9px] text-slate-400 flex items-center gap-0.5 justify-end"><Timer className="w-3 h-3" />{secs > 0 ? mt("Draw in") : mt("Result soon")}</div>
                  <div className={`font-mono font-black text-sm ${secs > 0 && secs < 600 ? "text-red-600 animate-pulse" : "text-emerald-700"}`} data-testid={`booked-countdown-${g.game.id}`}>{secs > 0 ? left(secs) : "—"}</div>
                </div>
              </div>
              <div className="mt-1.5 flex flex-wrap gap-1 items-center">
                {nums.slice(0, 8).map((x) => <span key={`${x.n}@${x.r}`} className="font-mono font-black text-[11px] px-1.5 py-0.5 rounded-md bg-emerald-100 text-emerald-900" data-testid="booked-number">{x.n}{x.r ? <span className="text-[9px] font-bold text-emerald-700"> @{fmtRate(x.r)}</span> : null}</span>)}
                {nums.length > 8 && <span className="text-[10px] font-bold text-emerald-700 px-1 py-0.5">+{nums.length - 8} {mt("more")}</span>}
                <span role="button" onClick={(e) => { e.stopPropagation(); shareTicket(g); }} className="ml-auto text-[10px] font-black text-white bg-[#25D366] px-2 py-0.5 rounded-full flex items-center gap-1" data-testid={`booked-share-${g.game.id}`}><Camera className="w-3 h-3" />{mt("Ticket photo")}</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
